<template>
  <ak-card-header class="flex flex--center">
    <h4 class="color-grey">Form Abandonment</h4>
    <div class="ml-auto">
      <div v-if="!loading && !noFormSelected">
        <ak-select
          v-model="selectedFormName"
          :options="formNames"
          inline-edit
          placeholder="Select Form"
          class="min-width-150 max-width-200"
          deselect-label=""
          select-label=""
          selected-label=""
        />
      </div>
    </div>
    <div v-if="isOwnAccount()">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
  </ak-card-header>

  <ak-card-body ref="containerBody" class="h-full" :size="'flush'">
    <!-- Loading screen -->
    <ak-loading-spinner v-if="loading" :size="'default'" class="m-auto" />
    <!-- No data message -->
    <ak-empty-state
      v-if="!loading && !noFormSelected && noData"
      icon="stats-bars"
      title="No data available"
    />
    <!-- No form selected -->
    <ak-empty-state v-if="!loading && noFormSelected" fa icon="list-check">
      <p>
        {{ isXEquals() ? 'X=Convert' : 'Akero' }} helps you learn where your
        {{ leadsBranding }} are abandoning your forms to improve your student
        conversion
      </p>
      <div v-if="forms.length > 0">
        <ak-select
          v-model="selectedFormName"
          :options="formNames"
          placeholder="Select Form"
          class="width-200 mx-auto"
          deselect-label=""
          select-label=""
          selected-label=""
        />
      </div>
      <div v-else>
        <ak-button
          color="primary"
          size="tiny"
          tag="router-link"
          class="mx-auto"
          :to="newFormLink"
          >Create Form</ak-button
        >
      </div>
    </ak-empty-state>
    <div
      v-if="!loading && !noData"
      class="flex flex--column my-auto min-width-200 ml"
    >
      <!-- Abandonment percentage total -->
      <div class="flex">
        <h4 class="mr-tiny">Abandonment %</h4>
        <p>- {{ formAbandonmentData.overview.abandonmentRate.toFixed(2) }}%</p>
      </div>
      <!-- Top Three Bottlenecks -->
      <h4 class="mb-tiny">Top Bottlenecks</h4>
      <div v-if="topThreeBottlenecks.length > 0">
        <div v-for="item in topThreeBottlenecks" :key="item.name">
          <p class="mb-tiny">
            {{ item.field.tag }} - {{ item.stat.percent.toFixed(2) }}%
          </p>
        </div>
      </div>
      <div v-else>
        <p class="mb-tiny">No bottlenecks found</p>
      </div>
    </div>
    <!-- Pie Chart -->
    <div ref="containerRef" class="overflow-y-hidden">
      <highcharts
        v-if="!loading && !noData"
        :options="chartOptions"
        class="m-auto"
      ></highcharts>
    </div>
  </ak-card-body>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
} from 'vue'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import stockInit from 'highcharts/modules/stock'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { isXEquals } from '@akerolabs/akero-vue/src/services/agency'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
stockInit(Highcharts)

export default defineComponent({
  name: 'AkWidgetFormAbandonment',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    highcharts: Chart,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },
    /**
     * @type {Function}
     * @description Update the widget state
     */
    setAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Get the widget state
     */
    getAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {String}
     * @description Region/container of the widget (eg. dashRight, dashLeft)
     */
    region: {
      type: String,
      default: '',
    },

    /**
     * @description The insights prop is used to specify the URL for the insights server
     * Allows it to work within all environments
     * @type {String}
     */
    insights: {
      type: String,
      default: 'https://insights.akerolabs.com',
    },
  },
  setup(props) {
    const loading = ref(true)
    const noData = ref(false)
    const forms = ref([])
    const selectedForm = ref(null)
    const noFormSelected = ref(false)
    const formAbandonmentData = ref({})
    const topThreeBottlenecks = ref([])
    const containerRef = ref(null)
    const containerBody = ref(null)

    const containerWidth = ref(null)
    const containerHeight = ref(null)

    /**
     * @description Account define contact branding
     */
    const leadsBranding = computed(() => {
      return entityBranding('leads.plural', 'leads')
    })

    /**
     * @description Computed property that returns an array of form names from the `forms` array.
     * @type {ComputedRef<string[]>}
     */
    const formNames = computed(() => forms.value.map((form) => form.title))

    /**
     * Computed property representing the name of the currently selected form.
     *
     * @type {ComputedRef<string>}
     * @description
     * The `selectedFormName` computed property provides the title of the currently selected form.
     * The getter function retrieves the title of the `selectedForm` if it exists, or an empty string if none is selected.
     * The setter function allows setting a new form name to be selected by searching for a matching form in the `forms` array.
     */
    const selectedFormName = computed({
      get: () => (selectedForm.value ? selectedForm.value.title : ''),
      set: (value) => {
        selectedForm.value = forms.value.find((form) => form.title === value)
      },
    })

    /**
     * Computed property representing the link to create a new form.
     *
     * @type {ComputedRef<{ name: string, params: { campaignId: null, preCampaignId: null, typeSlug: string } }>}
     * @description
     * The `newFormLink` computed property provides the link to the page for creating a new form.
     */
    const newFormLink = computed(() => ({
      name: 'formNew',
      params: {
        campaignId: null,
        preCampaignId: null,
        typeSlug: 'setup',
      },
    }))

    /**
     * @param {object} form
     * @description Set the state of the widget region
     */
    const setState = (form) => {
      props.setAccountState(`widgets.data.${props.region}._id`, form._id)
    }

    /**
     * @description Remove the state of the widget region
     */
    const removeState = () => {
      props.setAccountState(`widgets.data.${props.region}._id`, null)
      setDefaultValues()
    }

    /**
     * @description Check for a state in the widget region
     * If no state is found, set default values
     * If a state is found, set the selected form to the state
     */
    const checkForState = () => {
      const desiredForm = props.getAccountState('widgets.data.' + props.region)
      if (!desiredForm || !desiredForm._id || desiredForm._id === '') {
        setDefaultValues()
        noFormSelected.value = true
        return
      } else {
        selectedForm.value = forms.value.find(
          (form) => form._id === desiredForm._id,
        )
      }
    }

    /**
     * @description Set default values for the widget
     */
    const setDefaultValues = () => {
      loading.value = false
      noData.value = true
      formAbandonmentData.value = {}
      topThreeBottlenecks.value = []
    }

    /**
     * @description Watcher function that triggers when the `selectedForm` ref changes.
     * It updates the widget's state and behavior based on the new value of `selectedForm`.
     * @param {Object} newVal - The new value of the `selectedForm` ref.
     */
    watch(selectedForm, (newVal) => {
      if (!newVal) {
        noFormSelected.value = true
        removeState()
        return
      }
      noFormSelected.value = false
      setState(newVal)
      formSelected(newVal)
    })

    /**
     * @description Highcharts options object
     * @type {Ref<Highcharts.chartOptions>}
     */
    const chartOptions = ref({
      chart: {
        type: 'pie',
        height: containerHeight.value - 22, // -20 as chart is flush
        width: containerWidth.value - 240, // -240 for width of abandonment stats
        events: {
          load: function () {
            const chart = this
            chart.setSize(
              containerWidth.value - 240, // -20 as chart is flush
              containerHeight.value - 22, // -240 for width of abandonment stats
            )
          },
        },
      },
      title: {
        text: '',
      },
      exporting: {
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
        chartOptions: {
          legend: {
            enabled: false,
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
      legend: {
        layout: 'vertical',
        floating: true,
        align: 'right',
        verticalAlign: 'top',
        x: 0,
        y: 0,
        borderWidth: 0,
        navigation: {
          enabled: true,
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          showInLegend: true,
          innerSize: '60%',
          size: '80%',
          colors: ['#dadada', 'hsl(349, 74%, 53%)'],
          center: ['50%', '60%'],
          dataLabels: {
            enabled: false,
          },
        },
      },
      tooltip: {
        pointFormat: '<b>{point.percentage:.2f}% ({point.y})</b>',
      },
      loading: {
        style: {
          opacity: 1,
        },
      },
      credits: {
        enabled: false,
      },
    })

    /**
     * @description Fetch forms data from api and update the `forms` ref with the retrieved data.
     * If an error occurs during the fetch, display an error toast using the `ToastProgrammatic` service.
     * @async
     * @function
     */
    const fetchForms = async () => {
      try {
        const { data } = await request.get('/apps?', formsRequest.value)
        forms.value = data.data
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting form abandonment',
          type: 'is-danger',
        })
      }
    }

    /**
     * @description Computed property that returns the request object to fetch forms data from the server.
     * @type {ComputedRef<{ params: { sort: string, account: string, agency: string, per_page: number, type: string } }>}
     */
    const formsRequest = computed(() => ({
      params: {
        sort: '-createdOn',
        account: accountObj().id,
        agency: agencyObj().id,
        per_page: 0,
        type: 'embed',
      },
    }))

    /**
     * @description Fetch form abandonment data from insights for the selected form.
     * Updates the `formAbandonmentData` ref object.
     * If an error occurs during the fetch, display an error toast using the `ToastProgrammatic` service.
     * @async
     * @function
     */
    const fetchFormAbandonmentData = async () => {
      try {
        const startDate = '2010-01-01'
        const endDate = new Date()
        const baseURL = props.insights

        // Define the two asynchronous requests in parallel using Promise.all()
        const [overviewResponse, abandonmentResponse] = await Promise.all([
          request({
            method: 'POST',
            url: '/overview',
            baseURL: baseURL,
            headers: {},
            data: {
              appId: selectedForm.value.id,
              account: accountObj().id,
              deleted: false,
              startDate,
              endDate,
            },
          }),
          request({
            method: 'POST',
            url: '/bottlenecks/stats',
            baseURL: baseURL,
            headers: {},
            data: {
              appId: selectedForm.value.id,
              account: accountObj().id,
              agency: agencyObj().id,
              deleted: false,
              startDate,
              endDate,
            },
          }),
        ])

        // Update overview data and bottlenecks data in formAbandonmentData
        formAbandonmentData.value.overview = overviewResponse.data.data
        formAbandonmentData.value.bottlenecks = abandonmentResponse.data.data
      } catch {
        // Display error toast if fetch fails
        ToastProgrammatic.open({
          message: 'Error getting form abandonment',
          type: 'is-danger',
        })
      }
    }

    /**
     * @description Build bottleneck data for the widget based on the fetched form abandonment data.
     * Filters and sorts the form abandonment data to identify the top three bottlenecks
     * and updates the `topThreeBottlenecks` ref accordingly.
     * If the `formAbandonmentData` does not contain bottlenecks, or if the abandonment rate and lead count are both zero,
     * the `noData` ref is set to true and the `topThreeBottlenecks` ref is cleared.
     * @function
     */
    const buildBottleneckData = () => {
      // Check if bottlenecks data is available and if there is any form abandonment data
      if (
        !formAbandonmentData.value.bottlenecks ||
        (formAbandonmentData.value.overview.abandonmentRate === 0 &&
          formAbandonmentData.value.overview.leadCount === 0)
      ) {
        topThreeBottlenecks.value = []
        noData.value = true
      } else {
        // Filter and sort the bottlenecks data to get the top three bottlenecks
        const allParts = formAbandonmentData.value.bottlenecks.allParts.flat()
        const sortedParts = allParts
          .sort((a, b) => b.stat.percentage - a.stat.percentage)
          .map((part) => {
            if (part.name.length > 14) {
              // Truncate the name if it is longer than 14 characters
              part.name = part.name.substring(0, 14) + '...'
            }
            return part
          })
        const topThree = sortedParts.slice(0, 3)

        // Update the `topThreeBottlenecks` ref with the top three bottlenecks
        topThreeBottlenecks.value = topThree
        noData.value = false
      }
    }

    /**
     * @description Build the data for the chart based on the fetched form abandonment data and update the `chartOptions` ref.
     * @function
     */
    const buildData = () => {
      chartOptions.value.series = [
        {
          name: 'Form Abandonment',
          data: [
            {
              name: 'Submitted Form',
              y: formAbandonmentData.value.overview.leadCount,
            },
            {
              name: 'Abandoned',
              y: formAbandonmentData.value.overview.partialCount,
            },
          ],
        },
      ]
    }

    /**
     * Handle the selection of a form. This function is called when a form is selected from the dropdown.
     * It triggers the process of fetching form abandonment data, building bottleneck data, and chart data.
     * The loading state is set during data retrieval, and once the data is fetched, the loading state is set to false.
     * @async
     * @function
     */
    const formSelected = async () => {
      loading.value = true
      noData.value = false
      noFormSelected.value = false
      await fetchFormAbandonmentData()
      buildBottleneckData()
      buildData()
      loading.value = false
    }

    /**
     * @description Handle the resize event for the widget container.
     * If the loading state is false and the container reference is available, the function sets the loading state to true
     * and updates the container's width and height.
     * Triggering load causes the chart to redraw.
     * @function
     */
    const onResize = () => {
      if (!loading.value && containerRef.value) {
        loading.value = true

        setTimeout(() => {
          const container = containerBody.value.$el
          containerWidth.value = container.offsetWidth
          containerHeight.value = container.offsetHeight
        }, 200)

        // Toggle loading state after 2 seconds
        setTimeout(() => {
          loading.value = false
        }, 2000)
      }
    }

    /**
     * @description Perform actions when the component is mounted.
     * The function sets the loading state to true, fetches forms data, checks for a selected form state,
     * updates the container's width and height based on its offset dimensions, and adds a resize event listener to the window.
     * @async
     * @function
     */
    onMounted(async () => {
      loading.value = true
      await fetchForms()
      checkForState()
      const container = containerBody.value.$el
      if (container) {
        containerWidth.value = container.offsetWidth
        containerHeight.value = container.offsetHeight
      }
      window.addEventListener('resize', onResize)
    })

    /**
     * @description On unmount, remove resize listener
     * @function
     */
    onUnmounted(() => {
      // Remove resize listener
      window.removeEventListener('resize', onResize)
    })

    return {
      isOwnAccount,
      loading,
      forms,
      formNames,
      selectedForm,
      selectedFormName,
      topThreeBottlenecks,
      formAbandonmentData,
      chartOptions,
      containerRef,
      containerBody,
      containerWidth,
      containerHeight,
      noData,
      noFormSelected,
      isXEquals,
      leadsBranding,
      newFormLink,
    }
  },
})
</script>
